
import React, { createContext, useContext, useState, useRef } from 'react'

interface LoadingState {
  stage: 'idle' | 'campaigns' | 'analytics' | 'processing' | 'complete'
  progress: number
  currentBatch: number
  totalBatches: number
  estimatedTimeRemaining: number
  message: string
  startTime: number | null
}

interface LoadingContextType {
  loadingState: LoadingState
  updateLoadingState: (update: Partial<LoadingState>) => void
  resetLoadingState: () => void
  isLoading: boolean
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined)

const initialLoadingState: LoadingState = {
  stage: 'idle',
  progress: 0,
  currentBatch: 0,
  totalBatches: 0,
  estimatedTimeRemaining: 0,
  message: 'Ready',
  startTime: null
}

export function LoadingProvider({ children }: { children: React.ReactNode }) {
  const [loadingState, setLoadingState] = useState<LoadingState>(initialLoadingState)
  
  const updateLoadingState = (update: Partial<LoadingState>) => {
    setLoadingState(prev => ({ ...prev, ...update }))
  }
  
  const resetLoadingState = () => {
    setLoadingState(initialLoadingState)
  }
  
  const isLoading = loadingState.stage !== 'idle' && loadingState.stage !== 'complete'
  
  return (
    <LoadingContext.Provider value={{
      loadingState,
      updateLoadingState,
      resetLoadingState,
      isLoading
    }}>
      {children}
    </LoadingContext.Provider>
  )
}

export function useLoadingContext() {
  const context = useContext(LoadingContext)
  if (!context) {
    throw new Error('useLoadingContext must be used within a LoadingProvider')
  }
  return context
}
